import { useState, useEffect } from 'react';

import { getPrintPackageConfigurableItems } from '../../../../../../../../../util/print-packages';
import { getPrintPackageCartItemNonConfiguredItems } from './helpers';

const useAlbumPrintPackageCartConfigurator = ({
  albumPrintPackage,
  albumPrintPackageCartItem,
  onUpdateCartItem,
  onItemConfiguredComplete,
  allowMultiPose,
}) => {
  const [configurableItems, setConfigurableItems] = useState([]);
  const [activeConfigurableItem, setActiveConfigurableItem] = useState(null);
  const [activeConfig, setActiveConfig] = useState(null);

  useEffect(() => {
    setConfigurableItems(
      albumPrintPackageCartItem
        ? getPrintPackageCartItemNonConfiguredItems(
            albumPrintPackageCartItem,
            allowMultiPose,
          )
        : getPrintPackageConfigurableItems(albumPrintPackage, allowMultiPose),
    );
  }, [
    albumPrintPackageCartItem?.id,
    albumPrintPackageCartItem?.configurableItems,
  ]);

  const onItemConfigured = config => {
    if (!activeConfigurableItem || !albumPrintPackageCartItem) {
      return;
    }

    const configurableItemIndex = (
      albumPrintPackageCartItem.configurableItems ?? []
    ).findIndex(
      configurableItem =>
        configurableItem.printPackageItem?.id === activeConfigurableItem?.id,
    );

    if (configurableItemIndex === -1) {
      onUpdateCartItem(albumPrintPackageCartItem.id, {
        configurableItems: [
          ...(albumPrintPackageCartItem.configurableItems ?? []).filter(
            configurableItem =>
              // In case it comes from an edit step
              // We filter out existing configurable items with this
              // printPackageItem.
              configurableItem.printPackageItem?.id !==
              activeConfigurableItem?.id,
          ),
          {
            printPackageItem: activeConfigurableItem,
            config,
          },
        ],
      });
    } else {
      onUpdateCartItem(albumPrintPackageCartItem.id, {
        configurableItems: [
          ...(albumPrintPackageCartItem.configurableItems ?? []).slice(
            0,
            configurableItemIndex,
          ),
          {
            printPackageItem: activeConfigurableItem,
            config,
          },
          ...(albumPrintPackageCartItem.configurableItems ?? []).slice(
            configurableItemIndex + 1,
          ),
        ],
      });
    }

    setActiveConfigurableItem(null);

    const remainingConfigurableItems = configurableItems.filter(
      configurableItem => activeConfigurableItem.id !== configurableItem.id,
    );

    setConfigurableItems(remainingConfigurableItems);

    onItemConfiguredComplete({
      completeConfigurableItem: activeConfigurableItem,
      remainingConfigurableItems,
      config,
    });
  };

  const onItemsConfigured = configurations => {
    if (!albumPrintPackageCartItem) {
      return;
    }

    const newConfigurableItems = configurations.map(configurationItem => {
      const printPackageItem = configurableItems.find(
        configurableItem =>
          (configurationItem?.id &&
            configurableItem.id === configurationItem.id) ||
          (!configurationItem?.id &&
            configurableItem.print_product_configuration?.print_product?.id ===
              configurationItem.printProduct.id &&
            configurableItem.print_product_configuration?.print_product_size
              ?.id === configurationItem.printProductSize.id),
      );

      return {
        printPackageItem,
        config: { accountCard: configurationItem.accountCard },
      };
    });

    const newConfigurableItemsIds = newConfigurableItems.map(
      configurableItem => configurableItem.printPackageItem.id,
    );

    onUpdateCartItem(albumPrintPackageCartItem.id, {
      configurableItems: [
        ...(albumPrintPackageCartItem.configurableItems ?? []).filter(
          configurableItem =>
            // In case it comes from an edit step
            // We filter out existing configurable items with this
            // printPackageItem.
            !newConfigurableItemsIds.includes(
              configurableItem.printPackageItem?.id,
            ),
        ),
        ...newConfigurableItems,
      ],
    });

    setActiveConfigurableItem(null);

    const remainingConfigurableItems = configurableItems.filter(
      configurableItem =>
        !newConfigurableItemsIds.includes(configurableItem.id),
    );

    setConfigurableItems(remainingConfigurableItems);

    onItemConfiguredComplete({
      completeConfigurableItems: newConfigurableItems.map(
        configurableItem => configurableItem.printPackageItem,
      ),
      remainingConfigurableItems,
    });
  };

  const onConfigureNextItem = () => {
    const [configurableItem] = configurableItems;

    if (!configurableItem) {
      return;
    }
    // DO NOT remove the item from the require configure list until
    // the item config has been completed. Otherwise, users can leave and
    // we will miss the config reference.
    setActiveConfigurableItem(configurableItem);
  };

  const onConfigurationStart = () => {
    onConfigureNextItem();
  };

  const onConfigurationCancel = () => {
    setActiveConfigurableItem(null);
  };

  const onConfigurableItemUpdate = ({ printPackageItem, config }) => {
    setActiveConfigurableItem(printPackageItem);
    setActiveConfig(config);
  };

  return {
    activeConfigurableItem,
    activeConfig,
    configurableItems,
    onItemConfigured,
    onConfigurationStart,
    onConfigurationCancel,
    onConfigurableItemUpdate,
    onItemsConfigured,
  };
};

export default useAlbumPrintPackageCartConfigurator;
